// src/components/UpdateComponentModal.js
import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Select, List, Upload, notification } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { updateComponent } from '../actions/componentActions';
import { createJob, deleteJob } from '../actions/jobActions';
import { useDispatch, useSelector } from 'react-redux';

const { Option } = Select;

const UpdateComponentModal = ({ visible, onClose, components = [], onUpdated }) => {
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [componentName, setComponentName] = useState('');
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.components || {});

  // State for new job form
  const [jobForm] = Form.useForm();
  const [jobData, setJobData] = useState({
    executionTime: '',
    frequency: '',
    cpu: '',
    memory: '',
    serviceName: '',
  });
  const [manifestFile, setManifestFile] = useState(null);

  // Select first component by default
  useEffect(() => {
    if (components && components.length > 0) {
      setSelectedComponent(components[0]);
      setComponentName(components[0].name);
    } else {
      setSelectedComponent(null);
      setComponentName('');
    }
  }, [components]);

  const handleComponentSelect = (componentId) => {
    const comp = components.find((c) => c.id === componentId);
    setSelectedComponent(comp || null);
    setComponentName(comp ? comp.name : '');
  };

  // Update component name via API
  const handleUpdateComponentName = () => {
    if (!selectedComponent) return;

    dispatch(
      updateComponent({
        id: selectedComponent.id,
        data: { name: componentName },
      })
    )
      .unwrap()
      .then((updated) => {
        notification.success({
          message: 'Component Updated',
          description: 'Component name updated successfully.',
        });
        setSelectedComponent((prev) => ({ ...prev, name: updated.name }));
        if (onUpdated) {
    onUpdated();
  }
      })
      .catch((err) => {
        notification.error({
          message: 'Update Failed',
          description: err || 'Could not update component name.',
        });
      });
  };

  // Delete job via API
  const handleDeleteJob = (jobId) => {
    if (!selectedComponent) return;

    dispatch(deleteJob(jobId))
      .unwrap()
      .then(() => {
        notification.success({
          message: 'Job Deleted',
          description: `Job with id ${jobId} deleted successfully.`,
        });
        const updatedJobs = (selectedComponent.jobs || []).filter(
          (job) => job.id !== jobId
        );
        setSelectedComponent({ ...selectedComponent, jobs: updatedJobs });
        if (onUpdated) {
    onUpdated();
  }
      })
      .catch((err) => {
        notification.error({
          message: 'Delete Failed',
          description: err || 'Could not delete job.',
        });
      });
  };

  // Upload → keep only the latest file
  const handleFileChange = ({ file, fileList }) => {
    const latest = fileList[fileList.length - 1];
    setManifestFile(latest || null);
  };

  // Add job via API
  const handleAddJob = () => {
    if (!selectedComponent) return;

    if (!manifestFile) {
      notification.error({
        message: 'Manifest Required',
        description: 'Please upload a manifest file before creating the job.',
      });
      return;
    }

    const fileToSend = manifestFile.originFileObj || manifestFile;

    const formData = new FormData();
    // field name must match multer middleware in your backend, e.g. upload.single('manifest')
    formData.append('manifest', fileToSend);
    formData.append('executionTime', jobData.executionTime);
    formData.append('frequency', jobData.frequency);
    formData.append('cpu', jobData.cpu);
    formData.append('memory', jobData.memory);
    formData.append('serviceName', jobData.serviceName);

    dispatch(
      createJob({
        formData,
        componentId: selectedComponent.id,
      })
    )
      .unwrap()
      .then((createdJob) => {
        notification.success({
          message: 'Job Added',
          description: 'New job added successfully.',
        });

        const updatedJobs = [...(selectedComponent.jobs || []), createdJob];
        setSelectedComponent({ ...selectedComponent, jobs: updatedJobs });

        setJobData({
          executionTime: '',
          frequency: '',
          cpu: '',
          memory: '',
          serviceName: '',
        });
        setManifestFile(null);
        jobForm.resetFields();
        if (onUpdated) {
    onUpdated();
  }
      })
      .catch((err) => {
        notification.error({
          message: 'Creation Failed',
          description: err || 'Could not create job.',
        });
      });
  };

  const handleJobFieldChange = (field, value) => {
    setJobData({ ...jobData, [field]: value });
  };

  return (
    <Modal title="Update Components" open={visible} onCancel={onClose} footer={null}>
      <Form layout="vertical">
        <Form.Item label="Select Component">
          <Select
            value={selectedComponent ? selectedComponent.id : undefined}
            onChange={handleComponentSelect}
          >
            {components.map((comp) => (
              <Option key={comp.id} value={comp.id}>
                {comp.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>

      {selectedComponent && (
        <>
          <Form layout="vertical">
            <Form.Item label="Component Name">
              <Input
                value={componentName}
                onChange={(e) => setComponentName(e.target.value)}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" onClick={handleUpdateComponentName} loading={loading}>
                Update Component Name
              </Button>
            </Form.Item>
          </Form>

          <hr />

          <h5>Jobs</h5>
          {selectedComponent.jobs && selectedComponent.jobs.length > 0 ? (
            <List
              dataSource={selectedComponent.jobs}
              renderItem={(job) => (
                <List.Item
                  actions={[
                    <Button
                      danger
                      key="delete"
                      onClick={() => handleDeleteJob(job.id)}
                    >
                      Delete
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={job.serviceName || 'Job'}
                    description={`Execution Time: ${job.executionTime}, Frequency: ${job.frequency}`}
                  />
                </List.Item>
              )}
            />
          ) : (
            <p>No jobs available.</p>
          )}

          <hr />

          <h5>Add New Job</h5>
          <Upload
            beforeUpload={() => false}
            onChange={handleFileChange}
            fileList={manifestFile ? [manifestFile] : []}
          >
            <Button icon={<UploadOutlined />}>Upload Manifest</Button>
          </Upload>

          <Form layout="vertical" form={jobForm} onFinish={handleAddJob}>
            <Form.Item
              label="Execution Time*"
              name="executionTime"
              rules={[{ required: true, message: 'Required!' }]}
            >
              <Input
                value={jobData.executionTime}
                onChange={(e) => handleJobFieldChange('executionTime', e.target.value)}
              />
            </Form.Item>
            <Form.Item
              label="Frequency*"
              name="frequency"
              rules={[{ required: true, message: 'Required!' }]}
            >
              <Input
                value={jobData.frequency}
                onChange={(e) => handleJobFieldChange('frequency', e.target.value)}
              />
            </Form.Item>
            <Form.Item label="CPU" name="cpu">
              <Input
                value={jobData.cpu}
                onChange={(e) => handleJobFieldChange('cpu', e.target.value)}
              />
            </Form.Item>
            <Form.Item label="Memory" name="memory">
              <Input
                value={jobData.memory}
                onChange={(e) => handleJobFieldChange('memory', e.target.value)}
              />
            </Form.Item>
            <Form.Item label="Service Name" name="serviceName">
              <Input
                value={jobData.serviceName}
                onChange={(e) => handleJobFieldChange('serviceName', e.target.value)}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Add New Job
              </Button>
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
};

export default UpdateComponentModal;
